import { type ReactNode, useEffect } from 'react';

import { useGuideContext } from './GuideContext';
import {
  SectionDescription,
  SectionHeader,
  SectionIconWrapper,
  SectionTitle,
  SectionWrapper,
} from './GuideSection.styles';

interface GuideSectionProps {
  id: string;
  title: string;
  tocLabel?: string;
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
}

const GuideSection = ({ id, title, tocLabel, description, icon, children }: GuideSectionProps) => {
  const { register, unregister } = useGuideContext();

  // Sync label on every change without unregistering (avoids TOC flicker on language switch)
  useEffect(() => {
    register({ id, label: tocLabel ?? title });
  }, [id, title, tocLabel, register]);

  // Unregister only on unmount or id change
  useEffect(() => {
    return () => unregister(id);
  }, [id, unregister]);

  return (
    <SectionWrapper id={id}>
      <SectionHeader>
        {icon && <SectionIconWrapper>{icon}</SectionIconWrapper>}
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>
      {description && <SectionDescription $withIcon={!!icon}>{description}</SectionDescription>}
      {children}
    </SectionWrapper>
  );
};

export default GuideSection;
